import type { DifficultyLevel, EnrichmentRun } from './reading'
import type { PaginatedResponse } from './api'
import type { WordStatus } from './word'
import type { VocabularyStats } from './vocabulary'

export type AdminTab =
  | 'overview'
  | 'users'
  | 'words'
  | 'invitation_codes'
  | 'languages'
  | 'readings'
  | 'srs'
  | 'brain'
  | 'source_badges'
  | 'enrichment_runs'

export interface AdminUser {
  id: number
  email: string
  role: string
  confirmed: boolean
  confirmed_at: string | null
  otp_enabled: boolean
  languages_count: number
  words_tracked_count: number
  created_at: string
}

/** One enrolled language on a user's admin page. Mirrors UserLanguageSummaryQuery. */
export interface AdminUserLanguage {
  id: number
  name: string
  iso_code: string
  enrolled_at: string
  words_count: number
  due_count: number
}

/** Mirrors Api::Admin::UsersController#profile_json. */
export interface AdminUserProfile {
  id: number
  email: string
  role: string
  confirmed: boolean
  otp_enabled: boolean
  created_at: string
  last_active_at: string | null
  languages: AdminUserLanguage[]
}

/** One learner in one language. Mirrors UserLanguageStatsQuery. */
export interface AdminUserLanguageStats {
  user: { id: number; email: string }
  language: { id: number; name: string }
  enrolled_at: string | null
  last_active_at: string | null
  words: { total: number; due: number; by_status: Record<WordStatus, number> }
  reviews: {
    total: number
    last_30_days: number
    last_reviewed_at: string | null
    /** Keyed by rating, 1 (Again) to 4 (Easy). */
    ratings: Record<string, number>
  }
  readings_count: number
  custom_cards: { words: number; phrases: number }
  vocabulary: VocabularyStats
}

export interface AdminInvitationCode {
  id: number
  code: string
  active: boolean
  used_by_email: string | null
  used_at: string | null
  created_at: string
}

export interface AdminLanguage {
  id: number
  iso_code: string
  name: string
  users_count: number
  words_count: number
  readings_count: number
  created_at: string
}

export interface AdminWordSummary {
  id: number
  lemma: string
  part_of_speech: string
  language_name: string
  senses_count: number
  forms_count: number
  trackers_count: number
}

export interface AdminReading {
  id: number
  title: string
  difficulty_level: DifficultyLevel
  language_name: string
  author_email: string
  created_at: string
}

/** Tunable SRS curve parameters. Mirrors SrsSetting on the server. */
export interface SrsSettings {
  interval_modifier: number
  first_interval: number
  second_interval: number
  lapse_recovery: number
  know_well_interval: number
  mastery_threshold: number
  lapse_retention: number
  lapse_ease_penalty: number
  ease_recovery: number
  hard_multiplier: number
  easy_multiplier: number
  overdue_credit: number
  fuzz: number
}

export interface SrsLadderStep {
  days: number
  status: string
}

/** One rating's ladder: the interval after the nth consecutive review at it. */
export interface SrsLadderRow {
  rating: number
  label: string
  intervals: SrsLadderStep[]
}

export interface SrsPreview {
  mastery_threshold: number
  ladder: SrsLadderRow[]
}

/** What the scheduling lab plays a rating script forward from. */
export type LabSubject =
  | { kind: 'new' }
  | { kind: 'strength'; from_days: number }
  | { kind: 'word'; user_word_sense_id: number }

/** One simulated review. Mirrors Srs::Simulation#describe. */
export interface LabStep {
  review: number
  rating: number
  /** False for the Good reviews the server appends after the script runs out. */
  scripted: boolean
  /** Days from today the review happens on. */
  day: number
  interval_days: number
  ease_factor: number
  status: string
  lapsed_interval_days: number | null
}

/** A review already recorded for a real word; `day` is negative (the past). */
export interface LabPastReview {
  day: number
  rating: number
  interval_days: number
  ease_factor: number
}

export interface LabStart {
  day: number
  interval_days: number
  ease_factor: number
  lapsed_interval_days: number | null
  overdue_days: number
}

/** Mirrors Srs::Lab: the plan under the candidate settings, and under the saved ones. */
export interface LabPlan {
  start: LabStart
  history: LabPastReview[]
  steps: LabStep[]
  baseline: LabStep[]
}

/** A tracked sense: the admin's own in the lab, a learner's on their page. Mirrors Api::Admin::SrsWordsController. */
export interface LabWord {
  id: number
  lemma: string
  language_name: string
  gloss: string
  reviews_count: number
  last_reviewed_at: string | null
  status: WordStatus
  interval_days: number
  ease_factor: number
  next_review_at: string
  lapsed_interval_days: number | null
}

/**
 * Enrichment runs as the admin panel sees them.
 * Contract: specs/007-enrichment-runs-admin/contracts/admin-enrichment-runs-api.md
 */

export interface AdminEnrichmentRunReading {
  id: number
  title: string
  language_name: string
}

/**
 * The same run the reading page polls, plus the context a cross-reading view needs:
 * which text it belongs to and who started it.
 */
export interface AdminEnrichmentRun extends EnrichmentRun {
  reading: AdminEnrichmentRunReading
  started_by: string
}

export interface AdminEnrichmentRunDetail extends AdminEnrichmentRun {
  /**
   * False when the run has processed words but recorded none — it finished before
   * per-word outcomes existed. Distinct from a run that genuinely decided nothing,
   * and the cue to say so rather than render an empty list.
   */
  words_recorded: boolean
}

/** What became of one word in one run. Mirrors EnrichmentWordOutcome#outcome (Ruby). */
export type EnrichmentOutcome = 'enriched' | 'skipped' | 'unresolved' | 'proper_noun'

export interface EnrichmentWordOutcome {
  id: number
  word: string
  outcome: EnrichmentOutcome
}

/** `data`/`meta` are the paginated history; `active` is everything in flight. */
export interface EnrichmentRunFeed extends PaginatedResponse<AdminEnrichmentRun> {
  active: AdminEnrichmentRun[]
}

export interface EnrichmentRunDetailResponse {
  data: AdminEnrichmentRunDetail
  words: EnrichmentWordOutcome[]
  meta: PaginatedResponse<EnrichmentWordOutcome>['meta']
}

export const ENRICHMENT_OUTCOMES: EnrichmentOutcome[] = [
  'enriched',
  'skipped',
  'unresolved',
  'proper_noun',
]

export const ENRICHMENT_OUTCOME_LABELS: Record<EnrichmentOutcome, string> = {
  enriched: 'Enriched',
  skipped: 'Already present',
  unresolved: 'Not found',
  proper_noun: 'Names skipped',
}

/** Which counter on the run reports each outcome, so a chip can show its total. */
export const ENRICHMENT_OUTCOME_COUNTS: Record<
  EnrichmentOutcome,
  'enriched_count' | 'skipped_count' | 'unresolved_count' | 'proper_noun_count'
> = {
  enriched: 'enriched_count',
  skipped: 'skipped_count',
  unresolved: 'unresolved_count',
  proper_noun: 'proper_noun_count',
}

/** The headline counts the Overview section opens on. Mirrors Api::Admin::OverviewController. */
export interface AdminOverview {
  users_count: number
  languages_count: number
  readings_count: number
  words_count: number
  usable_invitation_codes_count: number
  active_enrichment_runs_count: number
}
